import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ContactLogService } from 'app/core/services/contact-log-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { validate } from 'app/shared/elements/ess-rule';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { ContactLogMethodReloadEvent, ContactLogOutcomeReloadEvent } from 'app/um/lib/events';
import { SaveMethod } from 'app/um/components/contact-log/save-method';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class ContactLogMethods extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected config: IContactLogConfig = {};

    protected contactLogMethod: UM.AuthContactLogMethod | null = null;
    protected contactLogMethods: ReadonlyArray<UM.AuthContactLogMethod> | null = null;

    private contactLogMethodReload: Subscription | null = null;

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;
    protected includeInactive = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $saveMethodModal!: IPopup;
    protected $form!: IForm;
    protected $methodForm!: Element;
    protected $saveMethod!: SaveMethod;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: ContactLogService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.service.configure(this.config);
        this.contactLogMethodReload = this.events.subscribe(ContactLogMethodReloadEvent, (e: ContactLogMethodReloadEvent) => {
            this.reload();
        });

        this.reload();
    }

    protected detached(): void {
        if(this.contactLogMethodReload) {
            this.contactLogMethodReload.dispose();
        }
    }

    ////****************** Events ***************************************************////
    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$saveMethodModal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid
        this.reload();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadContactLogMethods();
    }

    protected saveContactLogMethod(method: UM.AuthContactLogMethod): void {
        this.$saveMethod.method = method;
        this.contactLogMethod = method;

        this.$saveMethodModal.open();
    }

    protected saveContactLogMethodClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveMethodModal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async activateContactLogMethod(contactLogMethod: UM.AuthContactLogMethod): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(contactLogMethod.isActive) {
            const result = await confirmation.showDeactivate('core:contactLog.method.$long', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateContactLogMethod(contactLogMethod.id!, !contactLogMethod.isActive);
            isSuccessful = response.isSuccessful;
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            if(isSuccessful) this.events.publish(new ContactLogOutcomeReloadEvent());
            this.toaster.standard.activate('core:contactLog.method.$long', !contactLogMethod.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadContactLogMethods(): Promise<void> {
        const includes: string[] = [this.includeInactive ? 'outcomes:inactive' : 'outcomes']

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadContactLogMethods(options, includes);
            this.contactLogMethods = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
